import type { AtprotoSession } from '@/modules/atproto/types'
import { createId, db, nowIso } from '@/modules/db'

const ATPROTO_SESSION_KEY = 'wefa_atproto_session_v1'

interface StoredAtprotoSession extends AtprotoSession {
  playerId: string
  updatedAt: string
}

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

export function getStoredAtprotoSession(): StoredAtprotoSession | null {
  if (!isBrowser()) return null
  const raw = window.localStorage.getItem(ATPROTO_SESSION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredAtprotoSession
    if (
      typeof parsed.playerId !== 'string' ||
      typeof parsed.did !== 'string' ||
      typeof parsed.handle !== 'string' ||
      typeof parsed.accessJwt !== 'string' ||
      typeof parsed.refreshJwt !== 'string' ||
      typeof parsed.expiresAt !== 'string'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearStoredAtprotoSession(): void {
  if (!isBrowser()) return
  window.localStorage.removeItem(ATPROTO_SESSION_KEY)
}

export function setStoredAtprotoSession(playerId: string, session: AtprotoSession): void {
  if (!isBrowser()) return
  const payload: StoredAtprotoSession = {
    ...session,
    playerId,
    updatedAt: nowIso(),
  }
  window.localStorage.setItem(ATPROTO_SESSION_KEY, JSON.stringify(payload))
}

export async function persistAtprotoSession(
  playerId: string,
  session: AtprotoSession,
): Promise<void> {
  const timestamp = nowIso()
  const existing = await db.atprotoAccounts.where('playerId').equals(playerId).first()
  if (existing) {
    await db.atprotoAccounts.update(existing.id, {
      did: session.did,
      handle: session.handle,
      accessJwt: session.accessJwt,
      refreshJwt: session.refreshJwt,
      expiresAt: session.expiresAt,
      updatedAt: timestamp,
    })
  } else {
    await db.atprotoAccounts.add({
      id: createId(),
      playerId,
      did: session.did,
      handle: session.handle,
      accessJwt: session.accessJwt,
      refreshJwt: session.refreshJwt,
      expiresAt: session.expiresAt,
      createdAt: timestamp,
      updatedAt: timestamp,
    })
  }
}

export async function getPersistedAtprotoSession(playerId: string): Promise<AtprotoSession | null> {
  const row = await db.atprotoAccounts.where('playerId').equals(playerId).first()
  if (!row) return null
  return {
    did: row.did,
    handle: row.handle,
    accessJwt: row.accessJwt,
    refreshJwt: row.refreshJwt,
    expiresAt: row.expiresAt,
  }
}

export async function clearPersistedAtprotoSession(playerId: string): Promise<void> {
  const row = await db.atprotoAccounts.where('playerId').equals(playerId).first()
  if (!row) return
  await db.atprotoAccounts.delete(row.id)
}
